import { useEffect, useState, type ChangeEvent } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_USAGE_LEDGER_PDF_COVER_PREFS,
  loadUsageLedgerPdfCoverPrefs,
  saveUsageLedgerPdfCoverPrefs,
  type UsageLedgerPdfCoverPrefs,
} from '../../services/systems/sample-local/usageLedgerPdfCoverPrefs';

type Props = {
  show: boolean;
  onHide: () => void;
};

/**
 * 使用簿 PDF 表紙の表示項目・所属文言オーバーライド。
 * localStorage に保存（クラウド／ローカル共通）。
 */
export default function UsageLedgerPdfCoverSettingsModal({ show, onHide }: Props) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<UsageLedgerPdfCoverPrefs>(DEFAULT_USAGE_LEDGER_PDF_COVER_PREFS);

  useEffect(() => {
    if (show) setPrefs(loadUsageLedgerPdfCoverPrefs());
  }, [show]);

  const toggle =
    (key: keyof UsageLedgerPdfCoverPrefs) => (e: ChangeEvent<HTMLInputElement>) => {
      setPrefs((prev) => ({ ...prev, [key]: e.target.checked }));
    };

  const onSave = () => {
    saveUsageLedgerPdfCoverPrefs(prefs);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('reports.pdf_cover_settings_title', { defaultValue: '使用簿PDFの表紙設定' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">
          {t('reports.pdf_cover_settings_help', {
            defaultValue:
              '表紙に載せる項目を選べます。オフにした項目は PDF に出ません。所属などは空欄のときプロフィール（ローカル時は Local）を使います。',
          })}
        </p>
        <Form.Check
          type="switch"
          id="pdf-cover-logo"
          className="mb-2"
          label={t('reports.pdf_cover_show_logo', { defaultValue: '研助ロゴ' })}
          checked={prefs.showLogo}
          onChange={toggle('showLogo')}
        />
        <Form.Check
          type="switch"
          id="pdf-cover-brand"
          className="mb-2"
          label={t('reports.pdf_cover_show_brand', { defaultValue: 'kensuke-system ブランド行' })}
          checked={prefs.showBrandHeader}
          onChange={toggle('showBrandHeader')}
        />
        <Form.Check
          type="switch"
          id="pdf-cover-org"
          className="mb-2"
          label={t('reports.pdf_cover_show_org', { defaultValue: '所属・部署・作成者' })}
          checked={prefs.showOrgCard}
          onChange={toggle('showOrgCard')}
        />
        <Form.Check
          type="switch"
          id="pdf-cover-toc"
          className="mb-2"
          label={t('reports.pdf_cover_show_toc', { defaultValue: '目次' })}
          checked={prefs.showToc}
          onChange={toggle('showToc')}
        />
        <Form.Check
          type="switch"
          id="pdf-cover-note"
          className="mb-2"
          label={t('reports.pdf_cover_show_note', { defaultValue: '説明文' })}
          checked={prefs.showCoverNote}
          onChange={toggle('showCoverNote')}
        />
        <Form.Check
          type="switch"
          id="pdf-cover-sign"
          className="mb-3"
          label={t('reports.pdf_cover_show_sign', { defaultValue: '確認者・確認日欄' })}
          checked={prefs.showSignLines}
          onChange={toggle('showSignLines')}
        />
        {prefs.showOrgCard ? (
          <div className="border rounded p-2 bg-light">
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">
                {t('reports.pdf_cover_org_name', { defaultValue: '所属（組織）' })}
              </Form.Label>
              <Form.Control
                size="sm"
                value={prefs.organizationName}
                placeholder={t('reports.pdf_cover_use_profile', {
                  defaultValue: 'プロフィールの値を使用',
                })}
                onChange={(e) => setPrefs((p) => ({ ...p, organizationName: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">
                {t('reports.pdf_cover_dept_name', { defaultValue: '部署・研究室' })}
              </Form.Label>
              <Form.Control
                size="sm"
                value={prefs.departmentName}
                placeholder={t('reports.pdf_cover_use_profile', {
                  defaultValue: 'プロフィールの値を使用',
                })}
                onChange={(e) => setPrefs((p) => ({ ...p, departmentName: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">
                {t('reports.pdf_cover_prepared_by', { defaultValue: '作成者' })}
              </Form.Label>
              <Form.Control
                size="sm"
                value={prefs.preparedBy}
                placeholder={t('reports.pdf_cover_use_profile', {
                  defaultValue: 'プロフィールの値を使用',
                })}
                onChange={(e) => setPrefs((p) => ({ ...p, preparedBy: e.target.value }))}
              />
            </Form.Group>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          {t('common.cancel', { defaultValue: 'キャンセル' })}
        </Button>
        <Button variant="primary" size="sm" onClick={onSave}>
          {t('common.save', { defaultValue: '保存' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
